import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { assetsApi, Asset, CreateAssetRequest, UpdateAssetRequest, GetAssetsParams } from '../services/assetsApi'

interface AssetsState {
  assets: Asset[]
  pagination: {
    total: number
    page: number
    limit: number
  }
  loading: boolean
  error: string | null
}

interface AssetsActions {
  fetchAssets: (params?: GetAssetsParams) => Promise<void>
  createAsset: (assetData: CreateAssetRequest) => Promise<void>
  updateAsset: (assetId: string, assetData: UpdateAssetRequest) => Promise<void>
  deleteAsset: (assetId: string) => Promise<void>
  clearError: () => void
}

type AssetsStore = AssetsState & AssetsActions

export const useAssetsStore = create<AssetsStore>()(
  persist(
    (set) => ({
      assets: [],
      pagination: {
        total: 0,
        page: 1,
        limit: 10
      },
      loading: false,
      error: null,

      fetchAssets: async (params?: GetAssetsParams) => {
        set({ loading: true, error: null })
        try {
          const response = await assetsApi.getAssets(params)
          if (response.success && response.data) {
            const allAssets = response.data || []
            
            // Client-side pagination and search
            let filteredAssets = allAssets
            
            // Apply search filter if provided
            if (params?.search) {
              const searchTerm = params.search.toLowerCase()
              filteredAssets = allAssets.filter((asset: Asset) => 
                asset.name.toLowerCase().includes(searchTerm) ||
                asset.description.toLowerCase().includes(searchTerm) ||
                asset.type.toLowerCase().includes(searchTerm)
              )
            }
            
            // Calculate pagination
            const page = params?.page || 1
            const limit = params?.limit || 10
            const startIndex = (page - 1) * limit
            const endIndex = startIndex + limit
            const paginatedAssets = filteredAssets.slice(startIndex, endIndex)
            
            set({
              assets: paginatedAssets,
              pagination: {
                total: filteredAssets.length,
                page: page,
                limit: limit
              },
              loading: false,
              error: null,
            })
          } else {
            set({
              assets: [],
              pagination: { total: 0, page: 1, limit: 10 },
              loading: false,
              error: 'Failed to fetch assets',
            })
          }
        } catch (error: any) {
          set({
            assets: [],
            pagination: { total: 0, page: 1, limit: 10 },
            loading: false,
            error: error.message || 'Failed to fetch assets',
          })
        }
      },

      createAsset: async (assetData: CreateAssetRequest) => {
        set({ loading: true, error: null })
        try {
          const response = await assetsApi.createAsset(assetData)
          if (response.success && response.data) {
            const newAsset = response.data
            set(state => ({
              assets: [newAsset, ...state.assets],
              pagination: {
                ...state.pagination,
                total: state.pagination.total + 1
              },
              loading: false,
              error: null,
            }))
          } else {
            set({
              loading: false,
              error: 'Failed to create asset',
            })
          }
        } catch (error: any) {
          set({
            loading: false,
            error: error.message || 'Failed to create asset',
          })
        }
      },

      updateAsset: async (assetId: string, assetData: UpdateAssetRequest) => {
        set({ loading: true, error: null })
        try {
          const response = await assetsApi.updateAsset(assetId, assetData)
          if (response.success && response.data) {
            const updatedAsset = response.data
            set(state => ({
              assets: state.assets.map(asset => 
                asset._id === assetId ? updatedAsset : asset
              ),
              loading: false,
              error: null,
            }))
          } else {
            set({
              loading: false,
              error: 'Failed to update asset',
            })
          }
        } catch (error: any) {
          set({
            loading: false,
            error: error.message || 'Failed to update asset',
          })
        }
      },

      deleteAsset: async (assetId: string) => {
        set({ loading: true, error: null })
        try {
          const response = await assetsApi.deleteAsset(assetId)
          if (response.success) {
            set(state => ({
              assets: state.assets.filter(asset => asset._id !== assetId),
              pagination: {
                ...state.pagination,
                total: Math.max(0, state.pagination.total - 1)
              },
              loading: false,
              error: null,
            }))
          } else {
            set({
              loading: false,
              error: 'Failed to delete asset',
            })
          }
        } catch (error: any) {
          set({
            loading: false,
            error: error.message || 'Failed to delete asset',
          })
        }
      },

      clearError: () => {
        set({ error: null })
      },
    }),
    {
      name: 'assets-storage',
      partialize: (state) => ({ 
        assets: state.assets,
        pagination: state.pagination 
      }),
    }
  )
)
